"use client";

import React from "react";
import { Heart, House, Users } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent } from "@/components/ui/card";
export default function OurStory() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const textReveal1 = useRevealOnScroll({
    threshold: 0.1
  });
  const imageReveal1 = useRevealOnScroll({
    threshold: 0.1
  });
  const quoteReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const textReveal2 = useRevealOnScroll({
    threshold: 0.1
  });
  const imageReveal2 = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-story" className="bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 max-w-6xl">
        
        {}
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-primary font-serif text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            Our Family-First Home
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground font-light italic">
            Intimate Care, Family Atmosphere
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start mb-24">
          <div ref={textReveal1.ref} className={`lg:col-span-7 space-y-8 transition-all duration-1000 delay-100 ${textReveal1.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-lg md:text-xl leading-relaxed text-foreground/90 first-letter:text-7xl first-letter:font-serif first-letter:font-bold first-letter:text-primary first-letter:mr-3 first-letter:float-left first-letter:mt-[-8px]">
              Family First Senior Care is a small residential assisted living home in Silver Spring, Maryland, located in Montgomery County, serving up to five residents with personalized care and a family-like environment. We believe that aging shouldn't mean leaving the warmth and comfort of a true home behind, which is why our boutique residence is designed to offer the highest standard of individual attention.
            </p>
            <p className="text-lg leading-relaxed text-muted-foreground">
              Deeply rooted in the Montgomery County community, we provide a unique alternative to large, institutional facilities by fostering a place where residents are cherished members of a small household. By intentionally limiting our capacity to just five seniors, we ensure that every individual receives the dedicated respect, companionship, and personalized support they deserve in a setting that truly feels like home.
            </p>
          </div>
          
          <div ref={imageReveal1.ref} className={`lg:col-span-5 relative transition-all duration-1000 delay-300 ${imageReveal1.isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl relative">
              <img src="https://optimal-muskrat.10web.cloud/wp-content/uploads/2026/03/IMG_3590-scaled.jpg" alt="A warm residential assisted living exterior with inviting landscaping" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 ring-1 ring-inset ring-black/10 rounded-2xl"></div>
            </div>
            {}
            <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-secondary/20 rounded-full blur-3xl -z-10"></div>
          </div>
        </div>

        {}
        <div ref={quoteReveal.ref} className={`max-w-4xl mx-auto text-center mb-24 transition-all duration-1000 ${quoteReveal.isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
          <Heart className="w-12 h-12 text-secondary mx-auto mb-6 opacity-80" />
          <blockquote className="text-2xl md:text-4xl font-serif text-primary leading-tight italic">"In our small home, we don’t just provide care — we welcome residents into our family."</blockquote>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div ref={imageReveal2.ref} className={`lg:col-span-6 order-2 lg:order-1 relative transition-all duration-1000 delay-100 ${imageReveal2.isVisible ? "opacity-100 -translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4 mt-8">
                <img src="https://optimal-muskrat.10web.cloud/wp-content/uploads/2026/03/IMG_3657-scaled.jpg" alt="A welcoming garden setting with caregivers providing support" className="w-full aspect-square object-cover rounded-2xl shadow-md" />
                <Card className="bg-primary text-primary-foreground border-none shadow-lg">
                  <CardContent className="p-6 flex flex-col items-center text-center space-y-2">
                    <Users className="w-8 h-8 text-secondary mb-2" />
                    <span className="text-3xl font-bold font-serif">5</span>
                    <span className="text-sm font-medium uppercase tracking-wider opacity-90">Residents Only</span>
                  </CardContent>
                </Card>
              </div>
              <div className="space-y-4">
                <Card className="bg-accent text-accent-foreground border-none shadow-md">
                  <CardContent className="p-6 flex flex-col items-center text-center space-y-2">
                    <House className="w-8 h-8 text-primary mb-2" />
                    <span className="text-lg font-semibold">24/7</span>
                    <span className="text-sm opacity-80">Caregiver Support</span>
                  </CardContent>
                </Card>
                <img src="https://optimal-muskrat.10web.cloud/wp-content/uploads/2026/03/IMG_3640-scaled.jpg" alt="A warm home interior showing seniors and caregivers in a cozy setting" className="w-full aspect-[4/5] object-cover rounded-2xl shadow-md" />
              </div>
            </div>
          </div>

          <div ref={textReveal2.ref} className={`lg:col-span-6 order-1 lg:order-2 space-y-8 transition-all duration-1000 delay-300 ${textReveal2.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h3 className="text-3xl font-serif font-semibold text-foreground">Personalized Care for Just Five Residents</h3>
            <p className="text-lg leading-relaxed text-muted-foreground">Daily life in our small assisted living home is centered around the needs of just five residents. Our caregivers provide 24/7 support, home-cooked meals, and personalized attention that simply isn’t possible in larger facilities.</p>
            <p className="text-lg leading-relaxed text-muted-foreground">From quiet moments in the garden to shared meals in our dining room, every detail is designed to create a safe, comfortable, and family-like environment.</p>
          </div>
        </div>

      </div>
    </section>;
}